import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useGet } from "../../lib/hooks";
import { Button } from "../ui/button";
import { Pill } from "../Common";
import { RecordDialog } from "../RecordDialog";

const fields = (accounts, templates) => [
  { key: "title", label: "Title (bold, short)", required: true, full: true },
  { key: "message", label: "Short message (one line in the bar)", required: true, full: true },
  { key: "read_more_text", label: "Read more: full details (optional)", type: "textarea", full: true },
  { key: "read_more_url", label: "Read more link (optional, https://)", full: true },
  { key: "audience", label: "Who sees it", type: "select", options: [{ value: "all", label: "Everyone in every entity" }, { value: "selected", label: "Selected entities only" }] },
  { key: "account_ids", label: "Entities", type: "multicheck", options: accounts.map((a) => ({ value: a.id, label: a.name })), show: (f) => f.audience === "selected", full: true },
  { key: "starts_at", label: "Show from (optional)", type: "date" },
  { key: "ends_at", label: "Show until (optional)", type: "date" },
  { key: "active", label: "Active", type: "checkbox" },
  { key: "also_email", label: "Also send it by email (sent when created)", type: "checkbox" },
  { key: "email_template_id", label: "Email to use", type: "select", show: (f) => f.also_email, full: true,
    options: [{ value: "", label: "Standard announcement email" }, ...(templates || []).map((t) => ({ value: t.id, label: `Custom: ${t.name}` }))] },
];

export function Announcements({ accounts }) {
  const [list, reload] = useGet("/agency/announcements");
  const [templates] = useGet("/email-templates/custom");
  const [edit, setEdit] = useState(null);
  const save = async (f) => {
    try {
      if (edit.id) await api.put(`/agency/announcements/${edit.id}`, f);
      else await api.post("/agency/announcements", f);
      toast.success("Announcement saved"); reload();
    } catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const remove = async (a) => { if (!window.confirm(`Delete "${a.title}"?`)) return; await api.delete(`/agency/announcements/${a.id}`); reload(); };
  const who = (a) => (a.audience === "all" ? "Everyone" : a.account_ids.map((id) => accounts.find((x) => x.id === id)?.name).filter(Boolean).join(", ") || "No entities");
  return (
    <div className="space-y-4" data-testid="announcements-admin">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Announcements show as a slim gold bar at the top of every page. Each person can close it, and it stays closed for them.</p>
        <Button onClick={() => setEdit({ audience: "all", active: true, also_email: false, account_ids: [] })} data-testid="announcement-new-btn"><Plus className="h-4 w-4 mr-1" />New announcement</Button>
      </div>
      {list && !list.length && <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">No announcements yet.</div>}
      {list?.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-4" data-testid={`announcement-row-${a.id}`}>
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{a.title}</div>
            <div className="truncate text-sm text-muted-foreground">{a.message}</div>
            <div className="mt-1 text-[11px] text-slate-500">{who(a)}{a.starts_at && ` · from ${a.starts_at}`}{a.ends_at && ` · until ${a.ends_at}`}{a.email_log && ` · emailed ${a.email_log.sent}${a.email_log.failed ? `, ${a.email_log.failed} failed` : ""}`}</div>
          </div>
          <Pill tone={a.active ? "green" : "slate"}>{a.active ? "Active" : "Off"}</Pill>
          <Button size="icon" variant="ghost" onClick={() => setEdit(a)} data-testid={`announcement-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => remove(a)} data-testid={`announcement-delete-${a.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
        </div>
      ))}
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={edit.id ? "Edit announcement" : "New announcement"} fields={fields(accounts, templates)} initial={edit} onSave={save} />}
    </div>
  );
}
